import { useId, type ReactElement, type SVGProps } from 'react';

import {
  BADGE_FIT_TRANSFORM,
  BADGE_VIEWBOX,
  GRADIENT_GEOMETRY,
  GRADIENT_STOPS,
  GRADIENT_STOPS_BRIGHT,
  MARK_VIEWBOX,
  MARK_VIEWBOX_TIGHT,
  PATH_INNER,
  PATH_OUTER,
} from './logo-paths';

export type LogoVariant = 'mark' | 'badge' | 'coin';
export type LogoTone = 'gradient' | 'bright' | 'white' | 'ink' | 'currentColor';

export interface LogoProps extends Pick<SVGProps<SVGSVGElement>, 'className'> {
  /** Shape: bare two-path mark, a squircle badge tile, or a circular coin tile. */
  variant?: LogoVariant;
  /**
   * Fill treatment. `gradient`/`bright` paint the brand gradient; `white`/`ink`
   * are flat knockouts; `currentColor` inherits the text colour (for monochrome
   * contexts). Badge/coin tiles always carry the gradient fill with a white
   * knockout mark — the `tone` then selects the gradient flavour for the tile.
   */
  tone?: LogoTone;
  /** Rendered square size in px. */
  size?: number;
  /**
   * Accessible name. When provided the svg is exposed as `role="img"` with a
   * `<title>`; otherwise it is decorative (`aria-hidden`).
   */
  title?: string;
  /**
   * Crop the `mark` variant's viewBox to the ink, so `size` is the size of the
   * MARK rather than of a frame that is half empty — and so the ink is centred
   * in its own box. See `MARK_VIEWBOX_TIGHT` for the measurements.
   *
   * Defaults to `false` because every existing call site picked its `size` by
   * eye against the padded frame, and flipping the default would enlarge the
   * mark everywhere at once. New surfaces should pass it; ignored by the
   * `badge` and `coin` tiles, which already fit the ink themselves.
   */
  fit?: boolean;
}

const FLAT_FILL: Partial<Record<LogoTone, string>> = {
  white: '#ffffff',
  ink: '#0a0b0d',
  currentColor: 'currentColor',
};

/**
 * Syncanix brand mark. Self-contained per instance — its gradient def gets a
 * unique id via `useId` so any number of logos on a page never collide on the
 * `url(#…)` reference. Renders the two-path mark, a squircle badge, or a coin,
 * from the single vector source of truth in `logo-paths.ts`.
 */
export function Logo({
  variant = 'mark',
  tone = 'gradient',
  size = 32,
  title,
  fit = false,
  className,
}: LogoProps): ReactElement {
  const reactId = useId();
  const gradientId = `syncanix-logo-grad-${reactId}`;

  const stops = tone === 'bright' ? GRADIENT_STOPS_BRIGHT : GRADIENT_STOPS;
  const flatFill = FLAT_FILL[tone];
  // Tiles always carry the gradient; the bare mark honours a flat tone.
  const markUsesGradient = variant !== 'mark' || flatFill === undefined;
  const gradientRef = `url(#${gradientId})`;

  const a11y = title
    ? ({ role: 'img', 'aria-label': title } as const)
    : ({ 'aria-hidden': true } as const);

  const viewBox = variant === 'mark' ? (fit ? MARK_VIEWBOX_TIGHT : MARK_VIEWBOX) : BADGE_VIEWBOX;

  return (
    <svg
      className={className}
      width={size}
      height={size}
      viewBox={viewBox}
      xmlns="http://www.w3.org/2000/svg"
      focusable="false"
      {...a11y}
    >
      {title ? <title>{title}</title> : null}
      {markUsesGradient ? (
        <defs>
          <linearGradient
            id={gradientId}
            x1={GRADIENT_GEOMETRY.x1}
            y1={GRADIENT_GEOMETRY.y1}
            x2={GRADIENT_GEOMETRY.x2}
            y2={GRADIENT_GEOMETRY.y2}
            gradientUnits="userSpaceOnUse"
          >
            {stops.map((s) => (
              <stop key={s.offset} offset={s.offset} stopColor={s.color} />
            ))}
          </linearGradient>
        </defs>
      ) : null}

      {variant === 'badge' ? <rect width="100" height="100" rx="23" fill={gradientRef} /> : null}
      {variant === 'coin' ? <circle cx="50" cy="50" r="50" fill={gradientRef} /> : null}

      {variant === 'mark' ? (
        <>
          <path d={PATH_OUTER} fill={markUsesGradient ? gradientRef : flatFill} />
          <path d={PATH_INNER} fill={markUsesGradient ? gradientRef : flatFill} />
        </>
      ) : (
        // Badge/coin: white knockout of the mark over the gradient tile.
        <g transform={BADGE_FIT_TRANSFORM} fill="#ffffff">
          <path d={PATH_OUTER} />
          <path d={PATH_INNER} />
        </g>
      )}
    </svg>
  );
}
